import { deleteUser, getAuth, sendEmailVerification, signOut } from 'firebase/auth';
import Vue from 'vue';

import { LoginProvider, LoginProviderAccount } from '@/enums/login';
import { WebLocale } from '@/enums/web-locale';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { AuthResponse } from '@/interfaces/responses/auth';
import type { GettersConfig } from '@/interfaces/store';
import type { SubscriptionInfo } from '@/interfaces/subscriptions';
import type { OfferPresentationType, TitleObjectType } from '@/interfaces/titles';
import type { ApolloProvider } from 'vue-apollo';
import type { ActionTree, MutationTree } from 'vuex';

import { STORAGE_MIGRATION_REVISION } from '@/migrate-local-storage';

import { LoginProviderByFirebaseProviderId } from '@/enums/login';
import { OnboardingStatus } from '@/enums/onboarding-status';

import { iapCountries, updatePremiumStatus } from '@/helpers/iap-helper';

import { getAuthHeaders, getAxios } from '@/helpers/axios-helper';
import { saveConsents } from '@/helpers/cookiebot-helper';

import { getIdServiceVariables } from '@/helpers/id-helper';
import { getOnboardingStatus } from '@/helpers/onboarding-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { currentUser, generateToken } from '@/helpers/user-helper';
import { DataProcessingService, type DataProcessingServiceConsent } from '@/libraries/usercentrics/types';
import { LocalDataProcessingServices } from '@/helpers/usercentrics-helper';

import { ReadyType } from '@/stores/ready.store';

import {
	GetNewDeviceIdInputDocument,
	GetNewDeviceIdInputMutation,
	GetNewDeviceIdInputMutationVariables,
} from '@/graphql/mutation/GetNewDeviceId.mutation';

import { findSubscriptionBy } from '@/helpers/pro-web-helper';
import { getSettings, mutateSettings } from '@/helpers/settings-helper';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { hasAnyConsentsGiven } from '@/helpers/consent-helper';
import { useUserStats } from '@/helpers/composables/useUserStats';
import { generateHashedEmail } from '@/helpers/uuid-helper';
import { ProfileSpecificLink, UserRole } from '@/@types/graphql-types';
import { toUTCDateOnly } from '@/helpers/date-helper';
import { ValidExperimentName, experimentConfig, isBackendExperiment } from '@/libraries/experiment';
import { objectKeys } from '@/helpers/object-helper';
import { storeTCString } from '@/helpers/impression-tracker-helper';

const RECENT_SEARCH_TERMS_COUNT = 10;

export const EMAIL_HASH_KEY = 'hachee';

export const CONSENT_PARTNERS = {
	google: { name: 'Google', short: 'G', id: '1' },
	facebook: { name: 'Facebook', short: 'FB', id: '2' },
	twitter: { name: 'Twitter', short: 'TW', id: '4' },
	tiktok: { name: 'TikTok', short: 'TT', id: '6' },
	snapchat: { name: 'Snapchat', short: 'SC', id: '8' },
	allother: { name: 'All Other Providers', short: 'AO', id: '5' },
	emails: { name: 'Email', short: 'E', id: '7' },
	terms_and_conditions: { name: 'Terms and Conditions', short: 'TC', id: '9' },
} as const;

export type Consent = keyof typeof CONSENT_PARTNERS;

export type UpdateUserInfoPayload = {
	loginProvider: LoginProvider;
	name: string;
	email: string;
	emailVerified: boolean;
	photoURL: string;
};

export enum BuyBoxFilterOptions {
	ALL = 'all',
	SUBSCRIPTION = 'flatrate',
	FREE = 'free',
	FAST_TV = 'fast',
	BUY = 'buy',
	RENT = 'rent',
	CINEMA = 'cinema',
	PHYSICAL = 'physical',
}

export enum NewBuyBoxView {
	LIST = 'list',
	GRID = 'grid',
}

export interface UserInfo {
	email?: string;
	loginProvider?: LoginProvider;
	name?: string;
	emailVerified?: boolean;
	creationTime?: Date;
	photoURL?: string;
}

export enum premiumPurchaseFlowState {
	AUTH_STARTED = 'AUTH_STARTED',
	AUTH_SUCCESS = 'AUTH_SUCCESS',
	CHECKOUT = 'CHECKOUT',
}

export interface UserSettings {
	jw_consents: Record<Consent, boolean | null>;
	providers: string[];
	uc_consents: { id: string; consent: boolean }[] | null;
}

export interface ProfileInfo {
	uuid: string | null;
	firstName: string | null;
	lastName: string | null;
	city: string | null;
	country: string | null;
	bio: string | null;
	avatarUrl: string | null;
	website: ProfileSpecificLink | null | undefined;
	altWebsite: ProfileSpecificLink | null | undefined;
	instagram: ProfileSpecificLink | null | undefined;
	imdb: ProfileSpecificLink | null | undefined;
	x: ProfileSpecificLink | null | undefined;
	tiktok: ProfileSpecificLink | null | undefined;
	linkedin: ProfileSpecificLink | null | undefined;
	facebook: ProfileSpecificLink | null | undefined;
}

export interface UserSearches {
	recentSearchTerms: string[];
	recentlyVisitedTitleIds: string[];
}

export interface DiscoverySettings {
	continueWatchingHintDismissed: boolean;
}

// ExperimentSettings is used to define the `experiments` key in the local storage
export interface ExperimentSettings {
	// placeholderSetting: PlaceholderType;
}

export type UserState = {
	accessToken: string | undefined; // used to send for certain routes with authorization token
	discoverySettings: DiscoverySettings;
	enableProvidersFilter: boolean;
	hasCancelledPremium: boolean;
	hideSeenDislikedTitles: boolean;
	info: UserInfo;
	isPremium: boolean;
	profileIsComplete: boolean;
	isVerificationEmailSent: boolean;
	jwId: string | undefined;
	jwLoginId: string | undefined;
	lastUsedLoginProvider: string | undefined;
	loggedInProviders: LoginProvider[]; // list of provider names the user is authenticated with
	loginProgress: boolean;
	onboardingStatus: OnboardingStatus | null;
	persistedOverriddenLanguage: string | null;
	persistedWebLocale: WebLocale | null;
	permanentAudiencesSubgenres: string[];
	preferredExperiments: { [experiment: string]: { variant: string; sendSnowplowEvent: boolean } };
	preferences: {
		buyboxPresentationType: OfferPresentationType | null;
	};
	profileInfo: ProfileInfo | null;
	newBuyboxFilterType: BuyBoxFilterOptions;
	buyboxFilterTypeOptimistic: null | BuyBoxFilterOptions | OfferPresentationType;

	newBuyboxView: NewBuyBoxView;
	newBuyboxTooltipAcknowledged: boolean;

	// Temp: until we fully remove the old BB from the titlePage.
	loggedInNewBuyBoxView: NewBuyBoxView | null;

	// OLD BUYBOX EXP
	buyboxExpView: NewBuyBoxView;
	buyboxViewExpTooltipAcknowledged: boolean;
	// OLD BUYBOX EXP

	displayHomeSportsModule: boolean;
	premiumPurchaseFlowState: premiumPurchaseFlowState | null;
	searches: UserSearches;
	seenSurveys: Record<string, string[]>;

	settings: UserSettings; // user settings for non- and logged-in users
	settingsProvider: UserSettings['providers']; // only changes when settings.providers really have changed
	settingsJWConsents: UserSettings['jw_consents'];
	settingsUCConsents: UserSettings['uc_consents'];

	storageMigratedRevision: number; // used to determine when to do migration upon startup
	lastTimeMobileAppBannerWasClosedTS: number; // use to determine when mobile app banner was closed last time
	lastTimeListsOnboardingWasShownTS: number; // use to determine when onboarding was shown last time
	// Start COOKIE_BANNER_EXPERIMENT
	subscriptionInfo?: SubscriptionInfo;
	timeZone: string | undefined;
	// we need a way to reactively store all the DPS (data processing services) from Usercentrics.
	// then we can build an eligible DPS list by combining Usercentrics DPS and our usercentrics-helper.ts DPS.
	// with this we won't break any web app releases by changing them.
	usercentricsDPS: { id: string; name: string }[] | null;
	userRole: UserRole;
	isUserFirstVisit: boolean | null; // true: default value, null: on first visit, false: user has visited before

	showOldTitlePage: boolean; // used to determine if we should show the old title page or not

	// contains all the experiments that the user was naturally acquired to, we get this from the BE.
	acquiredExperiments: Partial<{
		[key in ValidExperimentName<true>]: string | null;
	}>;
	// contains all the preferred experiments that were set by the user using query params (?preferred_variant=[experiment-name]:[experiment-variant])
	_preferredExperiments: Partial<{
		[key in ValidExperimentName<true>]: string | null;
	}>;
	// contains all the experiments that were activated for the user ["experimentName:variantName"]
	activatedExperiments: string[];
	// stores the TCF string from UserCentrics
	uc_tcf: string | null;

	accountCreationTime: Date | null; // used to be set in the user cookie instead of the whole info object.
	optedOutConsent: boolean; // used to determine if the user has opted out of consents
};

export interface UserGetters {
	strictlyAllConsentsGranted: boolean;
	bestEffortId: string | undefined;
	buyboxPresentationType: UserState['preferences']['buyboxPresentationType'];
	newBuyboxFilterType: BuyBoxFilterOptions;
	newBuyboxView: NewBuyBoxView;
	newBuyboxTooltipAcknowledged: boolean;
	// OLD BUYBOX EXP
	buyboxExpView: NewBuyBoxView;
	buyboxViewExpTooltipAcknowledged: boolean;
	// OLD BUYBOX EXP
	displayHomeSportsModule: boolean;
	consents: Record<Consent, boolean | null> | null; // deprecated in favor if DPSconsents
	dpsConsents: Record<DataProcessingService, boolean | null> | null; // data processing consents (usercentrics)
	getPermanentAudiencesSubgenres: string[];
	hasCancelledPremium: boolean;
	isHideSeenDislikedTitlesEnabled: boolean;
	isLastUsedLoginProvider: (providerName?: string) => boolean;
	isLoggedIn: boolean;
	isPremium: boolean;
	showFreemium: boolean;
	isAdmin: boolean;
	isEditor: boolean;
	isProUser: boolean;
	isAccountVerified: boolean;
	profileInfo: ProfileInfo | null;
	profileIsComplete: boolean;
	isUserOnboarded: boolean;
	jwLoginId: string | undefined;
	subscriptionInfo: SubscriptionInfo | null;
	userInfo: UserInfo;
	userRole: UserRole;
	loggedInProviders: LoginProvider[];
	userSearches: UserState['searches'];
	loggedInNewBuyboxView: NewBuyBoxView;
	showOldTitlePage: boolean;
	tcString: string | null;
	userExperimentsInfo: {
		[experimentName: string]: {
			variant: string | null;
			preferred: boolean;
			activated: boolean;
			affiliationShortId: string | null;
		};
	};

	settings: UserSettings;
}
export type UserGettersConfig = GettersConfig<UserState, UserGetters>;

export const DEFAULT_USERSETTINGS = {
	// fill create map with consent partners valued with `null`
	jw_consents: (Object.keys(CONSENT_PARTNERS) as Consent[]).reduce((root, key) => {
		root[key] = null;
		return root;
	}, {} as Record<Consent, boolean | null>),
	providers: [] as string[],
	uc_consents: null,
} as UserSettings;

export const getInitialUserState = (): UserState => ({
	accessToken: undefined,
	discoverySettings: {
		continueWatchingHintDismissed: false,
	} as DiscoverySettings,
	enableProvidersFilter: false,
	hasCancelledPremium: false,
	hideSeenDislikedTitles: false,
	info: {},
	isPremium: false,
	profileIsComplete: false,
	isVerificationEmailSent: false,
	jwId: undefined,
	jwLoginId: undefined,
	lastUsedLoginProvider: undefined,
	loggedInProviders: [],
	loginProgress: false,
	onboardingStatus: null,
	persistedOverriddenLanguage: null,
	persistedWebLocale: null,
	permanentAudiencesSubgenres: [] as string[],
	preferredExperiments: {},
	preferences: {
		buyboxPresentationType: null,
	},
	profileInfo: null,
	newBuyboxFilterType: BuyBoxFilterOptions.ALL,
	buyboxFilterTypeOptimistic: null,

	newBuyboxView: NewBuyBoxView.LIST,
	newBuyboxTooltipAcknowledged: false,

	loggedInNewBuyBoxView: null,

	buyboxExpView: NewBuyBoxView.GRID,
	buyboxViewExpTooltipAcknowledged: false,

	displayHomeSportsModule: true,
	premiumPurchaseFlowState: null,
	searches: {
		recentSearchTerms: [],
		recentlyVisitedTitleIds: [],
	},
	seenSurveys: {},

	settings: DEFAULT_USERSETTINGS,
	settingsProvider: DEFAULT_USERSETTINGS.providers,
	settingsJWConsents: DEFAULT_USERSETTINGS.jw_consents,
	settingsUCConsents: DEFAULT_USERSETTINGS.uc_consents,

	storageMigratedRevision: STORAGE_MIGRATION_REVISION,
	lastTimeMobileAppBannerWasClosedTS: 0,
	lastTimeListsOnboardingWasShownTS: 0,
	timeZone: undefined,
	usercentricsDPS: null,
	userRole: UserRole.User,
	isUserFirstVisit: true,
	showOldTitlePage: false,

	acquiredExperiments: {},
	_preferredExperiments: {},
	activatedExperiments: [],
	uc_tcf: null,
	accountCreationTime: null,
	optedOutConsent: false, // used to determine if the user has opted out of consents
});

const defaultSubscriptionInfo: SubscriptionInfo = {
	id: '',
	type: 'regular',
	timeframe: 'monthly',
	prices: {
		DEFAULT: {
			price: 2.49,
			currency: 'EUR',
			priceId: '',
		},
	},
};

export type UserStore = {
	state: UserState;
	getters: UserGettersConfig;
};

// it needs to be a function that will later be called.
const state = getInitialUserState;

// store hashed email for snowplow flex-context event on title pages
async function saveEmailHash(email: string) {
	if (!process.client) return;

	const hash = await generateHashedEmail(email);
	localStorage.setItem(EMAIL_HASH_KEY, JSON.stringify(hash));
}

function removeEmailHash() {
	if (!process.client) return;

	localStorage.removeItem(EMAIL_HASH_KEY);
}

function removeTCString() {
	if (!process.client) return;

	sessionStorage.removeItem('tcString');
}

// GETTERS
// -------
const getters: UserGettersConfig = {
	userInfo: state => {
		return !state.accessToken ? {} : state.info;
	},
	loggedInProviders: state => {
		return state.loggedInProviders;
	},
	profileInfo: state => {
		return state.profileInfo;
	},
	settings: state => {
		return {
			jw_consents: state.settingsJWConsents,
			providers: state.settingsProvider,
			uc_consents: state.settingsUCConsents,
		};
	},
	buyboxPresentationType: state => state.preferences.buyboxPresentationType,
	newBuyboxFilterType: state => state.newBuyboxFilterType,
	newBuyboxView: state => state.newBuyboxView,
	newBuyboxTooltipAcknowledged: state => state.newBuyboxTooltipAcknowledged,
	// OLD BUYBOX EXP
	buyboxExpView: state => state.buyboxExpView,
	buyboxViewExpTooltipAcknowledged: state => state.buyboxViewExpTooltipAcknowledged,
	// OLD BUYBOX EXP
	displayHomeSportsModule: state => state.displayHomeSportsModule,
	getPermanentAudiencesSubgenres(state): string[] {
		return state.permanentAudiencesSubgenres;
	},
	isLoggedIn: state => state.loggedInProviders.length > 0,
	isLastUsedLoginProvider:
		state =>
		(providerName?: string): boolean => {
			return state.lastUsedLoginProvider === providerName;
		},
	bestEffortId: state => state.jwLoginId || state.jwId,
	jwLoginId: state => {
		// In some situations (purchases, JW Pro), we explicitly need a login ID to confirm
		// that the user has finished the auth flow and is logged in.
		return state.jwLoginId;
	},
	consents: (state, getters) => {
		// TODO Store should produce computed consents. Right now we assume some consents could be missing (see ConsentModal.vue)
		return getters.settings?.jw_consents || {};
	},
	tcString: state => {
		return state.uc_tcf;
	},
	dpsConsents: (state, getters) => {
		const explicitlyConsented = (getters.settings?.uc_consents || [])
			.filter(item => item.consent)
			.map(item => item.id);
		const excplicitlyRejected = (getters.settings?.uc_consents || [])
			.filter(item => !item.consent)
			.map(item => item.id);

		return (Object.keys(LocalDataProcessingServices) as DataProcessingService[]).reduce((root, serviceKey) => {
			const service = LocalDataProcessingServices[serviceKey];
			let consentValue = null;
			if (explicitlyConsented.includes(service.id)) {
				// user HAS consented to this service
				consentValue = true;
			} else if (excplicitlyRejected.includes(service.id)) {
				// user HAS rejected consent to this service
				consentValue = false;
			} else {
				// user HAS NOT consented nor rejected to this service
				consentValue = null;
			}
			root![serviceKey] = consentValue;
			return root;
		}, {} as UserGetters['dpsConsents']);
	},

	// In cases like 'injecting external trackers' we need to have the
	// user's full consent to ALL partners that we currently have set up as DPS.
	// So we can be as data privacy compliant as possible.
	//
	// We differentiate between two groups:
	// - UC countries: all partner consents must have `consent: true`
	// - non-UC countries: the `jw_consents.allother` value must be `true`
	strictlyAllConsentsGranted: (state, getters, rootState) => {
		if (hasUCConsentBanner(rootState.language.webLocale)) {
			const { uc_consents } = getters.settings;
			const totalDPS = state.usercentricsDPS ? state.usercentricsDPS.length : 0;
			// totalDPS is synced with UserCentrics, so it will be 0 in the beginning.
			// make sure they are loaded and not empty at 0.
			return totalDPS > 0 && state.usercentricsDPS?.length === uc_consents?.length;
		} else {
			return hasAnyConsentsGiven(getters.consents);
		}
	},

	userSearches: state => {
		return {
			recentSearchTerms: (state.searches.recentSearchTerms ?? []).slice(0, 10),
			recentlyVisitedTitleIds: (state.searches.recentlyVisitedTitleIds ?? []).slice(0, 10),
		};
	},
	isUserOnboarded: state => {
		const status = [
			OnboardingStatus.ONBOARDED,
			OnboardingStatus.USER,
			OnboardingStatus.GLOBAL_CLICKED_CTA,
			OnboardingStatus.SIGNED_UP,
		];
		return state.onboardingStatus != null && status.includes(state.onboardingStatus);
	},
	isHideSeenDislikedTitlesEnabled: state => {
		return state.hideSeenDislikedTitles;
	},
	isPremium: state => {
		return state.isPremium;
	},
	showFreemium: (_, getters, __, rootGetters) => {
		// show for premium-enabled users, no matter which country is set and for all platforms
		if (getters.isPremium) return true;

		// show for supported countries on mobile (individual features are responsible for locked/unlocked state)
		const appCountry = rootGetters['language/country'];
		if (iapCountries.includes(appCountry)) return true;

		// hide in all other cases
		return false;
	},
	isAdmin: state => {
		return state.userRole === UserRole.Admin;
	},
	isEditor: state => {
		return state.userRole === UserRole.Editor;
	},
	profileIsComplete: state => {
		return state.profileIsComplete;
	},
	isProUser: state => {
		return state.userRole === UserRole.Pro || state.isPremium;
	},
	isAccountVerified: (state, getters) => {
		const isAuthenticatedWithEmail = getters.userInfo?.loginProvider === LoginProvider.JUSTWATCH;
		return getters.isLoggedIn && (isAuthenticatedWithEmail ? !!state.info?.emailVerified : true);
	},
	hasCancelledPremium: state => {
		return state.hasCancelledPremium;
	},

	subscriptionInfo: state => {
		return state.subscriptionInfo || defaultSubscriptionInfo;
	},

	userRole: state => {
		return state.userRole;
	},

	loggedInNewBuyboxView: (state, getters, rootState) => {
		if (getters.isLoggedIn) {
			const defaultView =
				rootState['language'].webLocale === WebLocale.uk ? NewBuyBoxView.LIST : NewBuyBoxView.GRID;
			// ADDED THIS FOR COMPATIBILITY WITH OLD USER COOKIES (as they don't have the newly created accountCreationTime)
			const accountCreationTime = state?.accountCreationTime ?? state?.info?.creationTime;
			// this one might be temporary but we wanna only show by default the new buybox view to users that signed up after a 2025-05-20
			if (state?.loggedInNewBuyBoxView == null && accountCreationTime) {
				// converting dates to UCT to prevent any hydration errors.
				const creationDate = toUTCDateOnly(new Date(accountCreationTime));
				const comparisonDate = new Date(Date.UTC(2025, 4, 20));
				if (creationDate >= comparisonDate) {
					return NewBuyBoxView.LIST;
				}

				return defaultView;
			}
			return state.loggedInNewBuyBoxView ?? defaultView;
		}
		return state.newBuyboxView;
	},

	showOldTitlePage: (state, getters) => {
		if (!getters.isLoggedIn) {
			return false;
		}
		return state.showOldTitlePage;
	},
	userExperimentsInfo: state => {
		const activeVariants: UserGetters['userExperimentsInfo'] = {};
		for (const experimentName of objectKeys(state.acquiredExperiments)) {
			const activeVariant =
				experimentName in state._preferredExperiments
					? state._preferredExperiments[experimentName]
					: state.acquiredExperiments[experimentName];

			if (activeVariant) {
				activeVariants[experimentName] = {
					variant: activeVariant,
					preferred: experimentName in state._preferredExperiments,
					activated:
						state.activatedExperiments.includes(experimentName + ':' + activeVariant) ||
						isBackendExperiment(experimentName),
					affiliationShortId:
						experimentConfig?.[experimentName as ValidExperimentName]?.affiliationShortId || null,
				};
			}
		}
		return activeVariants;
	},
};

// ACTIONS
// -------
const actions: ActionTree<UserState, any> = {
	setAcquiredExperiments({ commit }, acquiredExperiments: UserState['acquiredExperiments']) {
		commit('SET_ACQUIRED_EXPERIMENTS', acquiredExperiments);
	},

	setNewPreferredExperiments({ commit }, preferredExperiments: UserState['_preferredExperiments']) {
		commit('SET_NEW_PREFERRED_EXPERIMENTS', preferredExperiments);
	},

	setActivatedExperiments({ commit }, activatedExperiments: string[]) {
		commit('SET_ACTIVATED_EXPERIMENTS', activatedExperiments);
	},

	registerActivatedExperiment: ({ state, commit, getters }, experimentName: string) => {
		const experimentVariant = getters['userExperimentsInfo']?.[experimentName]?.variant;
		const activeExperiment = experimentName + ':' + experimentVariant;

		if (experimentVariant && experimentName && !state.activatedExperiments.includes(activeExperiment)) {
			commit('ADD_ACTIVATED_EXPERIMENT', activeExperiment);
		}
	},

	fetchJwId: async ({ dispatch, state, getters }, apolloProvider: ApolloProvider) => {
		try {
			const variables = await getIdServiceVariables(state.jwId || null, getters.settings?.jw_consents || {});

			const results = await apolloProvider.defaultClient.mutate<
				GetNewDeviceIdInputMutation,
				GetNewDeviceIdInputMutationVariables
			>({
				mutation: GetNewDeviceIdInputDocument,
				variables: {
					input: {
						doNotTrack: variables.dnt,
						ids: [variables.ids],
					},
				},
			});

			if (results?.data?.getNewDeviceId.deviceId) {
				dispatch('setJwId', results?.data?.getNewDeviceId.deviceId);
			} else {
				TrackingHelper.trackEvent('idservice', {
					action: 'lookup',
					label: 'failed',
					nonInteraction: true,
				});
				throw 'no valid ID received';
			}
		} catch (error: any) {
			// Webapp can init, but better keep tracking as this is still a limited app experience (no settings)
			const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
			captureMessageForSentry('User Init Issue:', { error, where: '[user.store.ts] fetchJwId' }, 'error');
		}
	},

	setJwId: async ({ commit }, jwId: string) => {
		commit('SET_JW_ID', jwId);
		commit('ready/SET_READY', ReadyType.JW_ID, { root: true });
	},

	setBuyboxPresentationType(
		{ commit },
		payload: { presentationType: OfferPresentationType | null; additionalContexts?: SnowplowContext[] }
	) {
		const { presentationType, additionalContexts = [] } = payload;
		commit('SET_PREFERENCES_VALUE', presentationType);

		TrackingHelper.trackEvent(
			'presentationtype_change',
			{ action: presentationType ?? undefined },
			additionalContexts
		);
	},

	setNewBuyboxFilterType({ commit }, filterType: BuyBoxFilterOptions) {
		commit('SET_NEW_BUYBOX_FILTER_VALUE', filterType);
		// fail-safe that the optimistic value is not out of sync
		commit('SET_BUYBOX_FILTER_OPTIMISTIC_VALUE', filterType);
	},

	setBuyboxFilterTypeOptimistic({ commit }, filterType: null | BuyBoxFilterOptions | OfferPresentationType) {
		commit('SET_BUYBOX_FILTER_OPTIMISTIC_VALUE', filterType);
	},

	setNewBuyboxView({ commit, getters }, viewType: NewBuyBoxView) {
		if (getters.isLoggedIn) {
			commit('SET_LOGGED_IN_NEW_BUYBOX_VIEW', viewType);
		}
		commit('SET_NEW_BUYBOX_VIEW', viewType);
	},

	setNewBuyboxTooltipAcknowledged({ commit }) {
		commit('SET_NEW_BUYBOX_TOOLTIP_ACKNOWLEDGED');
	},

	// OLD BUYBOX EXP
	setBuyboxExpView({ commit }, viewType: NewBuyBoxView) {
		commit('SET_BUYBOX_EXP_VIEW', viewType);
	},

	setBuyboxViewExpTooltipAcknowledged({ commit }) {
		commit('SET_BUYBOX_VIEW_EXP_TOOLTIP_ACKNOWLEDGED');
	},
	// OLD BUYBOX EXP

	hideHomeSportsModule({ commit }) {
		commit('SET_DISPLAY_HOME_SPORTS_MODULE');
	},

	setProfileIsComplete({ commit }, profileIsComplete: boolean) {
		commit('SET_IS_PROFILE_COMPLETE', profileIsComplete);
	},

	/**
	 * Finds out if is login or signup
	 *
	 * @throws Error when user is not signed up yet
	 */
	verifyRegistration: async (): Promise<void> => {
		const user = await currentUser();

		if (!user) return;
		const tokenResult = await user.getIdTokenResult();

		if (tokenResult.claims.registration_error) throw `registration error: ${tokenResult.claims.registration_error}`;
		if (!tokenResult.claims.jw_login_id) throw 'not yet registered internally';
	},

	refreshToken: async ({ commit, dispatch, state }): Promise<string | undefined> => {
		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');

		const maxRetries = 2;
		let attempt = 0;
		let lastError: Error | null = null;
		let token: string | undefined;

		// Retry logic to handle transient failures (network blips, temporary service issues)
		while (attempt <= maxRetries && !token) {
			lastError = null;

			try {
				token = await generateToken(state.accessToken, state.jwId);

				if (!token) {
					throw new Error('Error getting refresh token');
				}

				commit('SET_AUTH_TOKEN', token);
				return token;
			} catch (error: any) {
				lastError = error;

				captureMessageForSentry(
					`[user.store.ts] refreshToken attempt ${attempt + 1}/${maxRetries + 1} failed`,
					{ error, where: 'user.store.ts refreshToken' },
					attempt >= maxRetries ? 'error' : 'warning'
				);
			}

			attempt++;

			// Wait before retrying (exponential backoff: 500ms, 1000ms, 2000ms)
			if (attempt <= maxRetries && !token) {
				await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempt - 1)));
			}
		}

		// All retries failed - determine if we should logout
		// Only keep user logged in if jwLoginId exists AND this is likely a transient Firebase issue
		// (not an actually invalid token)
		if (!token && state.jwLoginId) {
			captureMessageForSentry(
				'[user.store.ts] refreshToken failed after retries but jwLoginId exists - logging out to prevent broken state',
				{
					lastError,
					jwLoginId: state.jwLoginId,
					attempts: maxRetries + 1,
					where: 'user.store.ts refreshToken',
				},
				'error'
			);
		}

		// Always logout after retries fail to prevent broken state
		// Even if jwLoginId exists, an invalid token means user can't make authenticated requests
		if (!token) {
			try {
				await dispatch('logout');
			} catch (error: any) {
				captureMessageForSentry(
					'[user.store.ts] refreshToken: Error logging out after failed retries',
					{ error, where: 'user.store.ts refreshToken' },
					'error'
				);
			}
		}

		return token;
	},

	registerInternally: async ({ commit, dispatch, state, getters, rootGetters }) => {
		if (getters.isLoggedIn) return;
		commit('SET_LOGIN_PROGRESS', true);

		const user = await currentUser();

		if (!user) {
			throw new Error('[Firebase] internal registration failed: user not found');
		}

		// Get the firebase token, at this point we don't have the jw_login_id as part of claims
		const tokenResult = await user.getIdTokenResult(true);
		if (!tokenResult) {
			throw new Error('internal registration failed');
		}
		const { token } = tokenResult;

		try {
			await getAxios().post(
				`${JW_CONFIG.IDENTITY_URL}/register`,
				{},
				{
					headers: {
						Authorization: 'Bearer ' + token,
						// @ts-ignore
						['DEVICE-ID']: state.jwId,
					},
				}
			);

			// After registration, get the token result again, this should now contain the jw_login_id in claims.
			const newTokenResult = await user.getIdTokenResult(true);
			if (!newTokenResult || !newTokenResult.claims.jw_login_id) {
				throw new Error('internal registration failed');
			}

			await sendEmailVerification(user, {
				url: location.toString(),
			});
			commit('SET_AUTH_DATA', {
				connected_accounts: user.providerData.map((p: LoginProviderAccount) => {
					return {
						email: p!.email,
						login_provider: LoginProviderByFirebaseProviderId[p!.providerId],
					};
				}),
				device_id: state.jwId,
				login_id: newTokenResult.claims.jw_login_id,
				token: newTokenResult.token,
				loginProvider: LoginProviderByFirebaseProviderId[tokenResult.signInProvider as string],
			});

			await dispatch('loadSettings');
			await dispatch('saveSettings', { providers: rootGetters['constant/preselectedProviders'] });
			await dispatch('loadInfo');

			if (state.info?.email) await saveEmailHash(state.info.email);

			if (state.premiumPurchaseFlowState == premiumPurchaseFlowState.AUTH_STARTED) {
				// set state to continue purchase flow without user interaction
				commit('SET_PREMIUM_PURCHASE_FLOW_STATE', premiumPurchaseFlowState.AUTH_SUCCESS);
			}

			Vue.$jw.ready?.setReady(ReadyType.LOGIN_FINISHED);
		} catch (err) {
			throw err;
		} finally {
			commit('SET_LOGIN_PROGRESS', false);
		}
	},
	updateUserPremiumStatus: async ({ dispatch, state }) => {
		const { newPremiumStatus, subscriptionId } = await updatePremiumStatus(state.jwLoginId);
		await dispatch('setPremiumStatus', newPremiumStatus);
		await dispatch('setSubscriptionInfo', subscriptionId);
	},
	signInInternally: async ({ commit, dispatch, state }) => {
		commit('SET_LOGIN_PROGRESS', true);

		const user = await currentUser();

		if (!user) {
			commit('SET_LOGIN_PROGRESS', false);
			throw new Error('no firebase user');
		}
		try {
			const tokenResult = await user.getIdTokenResult(true);
			if (!tokenResult || !tokenResult.claims.jw_login_id) {
				throw new Error('need JW login ID for signing in internally');
			}

			await getAxios().post(
				`${JW_CONFIG.IDENTITY_URL}/sign_in`,
				{},
				{
					headers: {
						Authorization: 'Bearer ' + tokenResult.token,
						// @ts-ignore
						['DEVICE-ID']: state.jwId,
					},
				}
			);

			commit('SET_AUTH_DATA', {
				connected_accounts: user.providerData.map((p: LoginProviderAccount) => {
					return {
						email: p!.email,
						login_provider: LoginProviderByFirebaseProviderId[p!.providerId],
					};
				}),
				device_id: state.jwId,
				login_id: tokenResult.claims.jw_login_id,
				token: tokenResult.token,
				loginProvider: LoginProviderByFirebaseProviderId[tokenResult.signInProvider as string],
			});

			// update settings
			await dispatch('loadSettings');

			// only MOBILE
			await dispatch('updateUserPremiumStatus');
			await dispatch('loadInfo');

			if (state.info?.email) await saveEmailHash(state.info.email);

			// if sign-in/up was part of premium purchase flow
			if (state.premiumPurchaseFlowState == premiumPurchaseFlowState.AUTH_STARTED) {
				if (state.isPremium) {
					// user already has Premium, abort purchase flow
					commit('SET_PREMIUM_PURCHASE_FLOW_STATE', null);
				} else {
					// set state to continue purchase flow without user interaction
					commit('SET_PREMIUM_PURCHASE_FLOW_STATE', premiumPurchaseFlowState.AUTH_SUCCESS);
				}
			}

			Vue.$jw.ready?.setReady(ReadyType.LOGIN_FINISHED);
		} catch (err) {
			throw err;
		} finally {
			commit('SET_LOGIN_PROGRESS', false);
		}
	},

	/**
	 * Logout for all providers
	 */
	logout: async ({ dispatch, commit, state, rootState, rootGetters }, trackLogout = false) => {
		// Call signout to register in the consumer api that the user has signed out
		if (trackLogout) {
			if (!state.accessToken && !state.jwId) {
				console.warn('Trying to logout without jwId');
				return;
			}

			const headers = await getAuthHeaders(state.accessToken, { ['DEVICE-ID']: state.jwId });
			await getAxios().post(`${JW_CONFIG.IDENTITY_URL}/sign_out`, {}, { headers });
		}

		try {
			await signOut(getAuth());
		} catch (err) {
			console.error('[Firebase] Signout error: ', err);
		}

		commit('LOGOUT');

		await dispatch('setPremiumStatus', false);
		await dispatch('setProfileInfo', null);
		await dispatch('setProfileIsComplete', false);
		await dispatch('setSubscriptionInfo', null);
		await dispatch('loadSettings');

		removeEmailHash();
		removeTCString();

		const locale = rootState.language.webLocale;
		const newOnboardingStatus = getOnboardingStatus(locale, false, rootState.routing.activeRoute?.path);
		// To reset onboarding status
		// Need to check if is loggedIn to avoid onboardingStatus reset
		// in case of failed login attempt (calls logout in that case)
		// update settings

		await dispatch('saveOnboardingStatus', newOnboardingStatus);
		await dispatch('saveSettings', { providers: rootGetters['constant/preselectedProviders'] });
	},

	sendCode: async ({ state }, code: string) => {
		if (state.accessToken) {
			const headers = await getAuthHeaders(state.accessToken);
			return getAxios().post(`${JW_CONFIG.USER_URL}/tv/connect`, { code }, { headers });
		}
	},

	hideMobileBanner({ commit }) {
		commit('SET_MOBILE_BANNER_HIDDEN', true);

		TrackingHelper.trackEvent('promotionbanner', {
			action: 'hide_click',
		});
	},

	// @deprecated: use saveDPSConsents for newer consent management
	saveConsents({ dispatch }, consents: Record<Consent, boolean>) {
		dispatch('saveSettings', { jw_consents: consents });
		TrackingHelper.trackConsent(consents);
		TrackingHelper.trackEvent('consent', {
			action: 'saved',
			nonInteraction: true,
		});
		saveConsents(consents);
		console.log('@todo replace with UsercentricsHelper.saveConsents(consents)');
	},

	updateAgreedToConsent({ commit }) {
		commit('SET_AGREED_TO_CONSENT');
	},
	/**
	 * we only locally save DPS consents that either:
	 * - have been answered explicitly
	 * - are true (usually being 'essential')
	 *
	 * note: it's possible that `usercentricsDpsConsents` has newer DPS that the local DPS (usercentrics-helper@LocalDataProcessingServices) don't have.
	 * in the getters `dpsConsents` we ignore DPS (either from UC or local) that aren't present in both sets.
	 */
	saveDPSConsents({ dispatch, state, getters }, usercentricsDpsConsents: DataProcessingServiceConsent[]) {
		const explicitConsents = usercentricsDpsConsents.filter(consent => consent.hasAnswered);
		const uc_consents = explicitConsents.map(item => ({ id: item.id, consent: item.status }));

		// we want to prevent saving the uc_consents if they haven't changed.
		const getStringifiedConsent = (consents: UserSettings['uc_consents']) =>
			JSON.stringify([...(consents || [])].sort((a, b) => b.id.localeCompare(a.id)));

		if (getStringifiedConsent(getters.settings.uc_consents) !== getStringifiedConsent(uc_consents)) {
			dispatch('saveSettings', { uc_consents });

			// Store the GDPR Consent TC string if it has changed
			storeTCString();
		}
	},

	// Stores the TCF string generated by UserCentrics when consents are saved.
	setTCString: ({ commit }, tcString: string | null) => {
		commit('SET_UC_TCF', tcString);
	},

	async loadSettings({ state, commit, rootGetters }) {
		await Vue.$jw.ready?.waitFor(ReadyType.JW_ID);
		if (!state.jwId) return false;

		const accessToken = state.accessToken;
		const locale = rootGetters['language/locale'];
		const settings = await getSettings(state.jwId, locale, accessToken);
		commit('SET_SETTINGS', settings);
		commit('ready/SET_READY', ReadyType.USER_SETTINGS_LOADED, { root: true });
	},

	async loadInfo({ state, commit }) {
		if (!state.accessToken) {
			commit('SET_USER_INFO', {});
			commit('SET_ACCOUNT_CREATION_TIME', null);
			return;
		}

		const user = await currentUser();

		const info = {
			name: user?.displayName || '',
			email: user?.email || '',
			loginProvider: state.info.loginProvider,
			emailVerified: user?.emailVerified,
			creationTime: user?.metadata?.creationTime,
			photoURL: user?.photoURL || '',
		};
		commit('SET_USER_INFO', info);
		commit('SET_ACCOUNT_CREATION_TIME', user?.metadata?.creationTime);
	},

	setLoginProviders({ commit }, loginProviders: LoginProvider[]) {
		commit('SET_LOGIN_PROVIDERS', loginProviders);
	},

	updateUserInfo({ commit }, userInfoPayload: UpdateUserInfoPayload) {
		commit('UPDATE_USER_INFO', userInfoPayload);
	},

	async deleteAccountPermanently({ dispatch }) {
		const user = await currentUser();
		if (!user) throw 'not logged in to Firebase';

		try {
			await deleteUser(user);
			await dispatch('logout');
		} catch (e) {
			const err: any = e;
			if (err.code !== 'auth/requires-recent-login') {
				const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
				captureMessageForSentry(
					'Error deleting user account:',
					{ error: err, where: '[user.store.ts] deleteAccountPermanently' },
					'error'
				);
			}
			throw e;
		}
	},

	async saveSettings({ commit, state, getters, rootGetters }, partialSettings: Partial<UserSettings> = {}) {
		const locale = rootGetters['language/locale'];
		const settings = await mutateSettings(state.jwId, state.accessToken, locale, getters.settings, partialSettings);
		commit('SET_SETTINGS', settings);
	},

	saveOnboardingStatus({ commit }, status: OnboardingStatus) {
		commit('SAVE_ONBOARDING_STATUS', status);
	},

	savePersistedOverriddenLanguage({ commit }, language: string) {
		commit('SET_PERSISTED_OVERRIDDEN_LANGUAGE', language);
	},

	saveSearchTerm({ commit }, searchTerm: string) {
		if (searchTerm.trim().length > 0) commit('SAVE_SEARCH_TERM', searchTerm);
	},
	saveVisitedTitleId({ commit }, visitedTitleId: string) {
		commit('SAVE_VISITED_TITLE_ID', visitedTitleId);
	},
	clearSearchHistory({ commit }) {
		commit('CLEAR_SEARCH_HISTORY');
	},
	async setPremiumStatus({ commit, dispatch, getters }, value: boolean) {
		if (!value && getters.isPremium) {
			await dispatch('filter/resetPremiumFilters', {}, { root: true });
			await dispatch('setHideSeenDislikedTitles', false);
		}
		commit('SET_IS_PREMIUM', value);
	},
	async setSubscriptionInfo({ commit }, subscriptionId: string) {
		const subscriptionInfo = findSubscriptionBy('id', subscriptionId);
		commit('SET_SUBSCRIPTION_INFO', subscriptionInfo);
	},
	setPremiumCancellationStatus({ commit }, value: boolean) {
		commit('SET_HAS_CANCELLED_PREMIUM', value);
	},
	setPremiumPurchaseFlowState({ commit }, value: premiumPurchaseFlowState | null) {
		commit('SET_PREMIUM_PURCHASE_FLOW_STATE', value);
	},
	setHideSeenDislikedTitles({ commit }, value: boolean) {
		commit('SET_HIDE_SEEN_DISLIKE_TITLES', value);
	},
	setTimeZone({ commit }, value: string) {
		commit('SET_TIME_ZONE', value);
	},
	setIsVerificationEmailSent({ commit }, isVerificationEmailSent: boolean) {
		commit('SET_IS_VERIFICATION_EMAIL_SENT', isVerificationEmailSent);
	},
	setLastTimeMobileAppBannerWasClosedTS({ commit }, timeStamp: number) {
		commit('SET_LAST_TIME_MOBILE_APP_BANNER_WAS_CLOSED_TS', timeStamp);
	},
	setLastTimeListOnboardingWasShownTS({ commit }, timeStamp: number) {
		commit('SET_LAST_TIME_LIST_ONBOARDING_WAS_SHOWN_TS', timeStamp);
	},
	hideConsentOverlay: ({ commit }) => {
		commit('HIDE_CONSENT_OVERLAY');
	},
	updateUserVisitStatus: async ({ commit, state }) => {
		if (state.isUserFirstVisit === true) {
			commit('SET_USER_FIRST_VISIT', null);
		} else if (state.isUserFirstVisit === null) {
			commit('SET_USER_FIRST_VISIT', false);
		}
	},
	setPermanentAudiencesSubgenres({ commit }, audiences: string[]) {
		commit('SET_PERMANENT_AUDIENCES_SUBGENRES', audiences);
		TrackingHelper.refreshPermanentAudiencesSubgenres(audiences);
	},
	setProfileInfo({ commit }, profileInfo: ProfileInfo) {
		commit('SET_PROFILE_INFO', profileInfo);
	},
	resetPermanentAudiencesSubgenres({ commit }) {
		commit('SET_PERMANENT_AUDIENCES_SUBGENRES', []);
	},
	setLoginProgress({ commit }, loginProgress: boolean) {
		commit('SET_LOGIN_PROGRESS', loginProgress);
	},
	setUserRole({ commit }, userRole: UserRole) {
		commit('SET_USER_ROLE', userRole);
	},
	toggleShowOldTitlePage({ commit }) {
		commit('TOGGLE_SHOW_OLD_TITLE_PAGE');
	},
	setUsercentricsDPS({ commit }, dataProcessingServices: { id: string; name: string }[]) {
		commit('SET_USERCENTRICS_DPS', dataProcessingServices);
	},
	setDiscoverySettings({ commit }, discoverySettings: DiscoverySettings) {
		commit('SET_DISCOVERY_SETTINGS', discoverySettings);
	},
	setOptedOutConsent({ commit }, optedOut: boolean) {
		commit('SET_OPTED_OUT_CONSENT', optedOut);
	},
};

// MUTATIONS
// -------
const mutations: MutationTree<UserState> = {
	SET_JW_ID: (state, jwId: string) => {
		Vue.set(state, 'jwId', jwId);
	},

	ADD_SEEN_SURVEY(
		state,
		{ surveyId, objectType, objectId }: { surveyId: number; objectType?: TitleObjectType; objectId?: number }
	) {
		if (!(surveyId in state.seenSurveys)) {
			state.seenSurveys[surveyId] = [];
		}
		if (objectType && objectId) {
			const id = `${objectType}-${objectId}`;
			if (state.seenSurveys[surveyId].indexOf(id) === -1) {
				state.seenSurveys[surveyId].push(id);
			}
		}
	},

	SET_PREFERENCES_VALUE(state, buyboxPresentationType: OfferPresentationType | null) {
		state.preferences.buyboxPresentationType = buyboxPresentationType;

		// Update Sentry tag
		import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
			updateSentryTags({
				buybox_presentation_type: buyboxPresentationType === null ? 'best' : buyboxPresentationType,
			});
		});
	},

	SET_NEW_BUYBOX_FILTER_VALUE(state, filterType: BuyBoxFilterOptions) {
		state.newBuyboxFilterType = filterType;

		// Update Sentry tag
		import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
			updateSentryTags({ new_buybox_filter_type: filterType });
		});
	},

	SET_BUYBOX_FILTER_OPTIMISTIC_VALUE(state, filterType: BuyBoxFilterOptions) {
		state.buyboxFilterTypeOptimistic = filterType;
	},

	SET_NEW_BUYBOX_VIEW(state, viewType: NewBuyBoxView) {
		state.newBuyboxView = viewType;

		// Update Sentry tag
		import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
			updateSentryTags({ buybox_view: viewType });
		});
	},

	SET_NEW_BUYBOX_TOOLTIP_ACKNOWLEDGED(state) {
		state.newBuyboxTooltipAcknowledged = true;

		// Update Sentry tag
		import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
			updateSentryTags({ buybox_tooltip_acknowledged: true });
		});
	},
	SET_BUYBOX_VIEW_EXP_TOOLTIP_ACKNOWLEDGED(state) {
		state.buyboxViewExpTooltipAcknowledged = true;
	},
	SET_LOGGED_IN_NEW_BUYBOX_VIEW(state, value: NewBuyBoxView) {
		state.loggedInNewBuyBoxView = value;
	},
	SET_BUYBOX_EXP_VIEW(state, value: NewBuyBoxView) {
		state.buyboxExpView = value;

		// Update Sentry tag
		import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
			updateSentryTags({ buybox_exp_view: value });
		});
	},

	SET_DISPLAY_HOME_SPORTS_MODULE(state) {
		state.displayHomeSportsModule = false;
	},

	SET_LOGIN_PROGRESS: (state, value: boolean) => {
		if (!value) useUserStats().refetchUserStats();
		state.loginProgress = value;
	},

	SET_IS_PROFILE_COMPLETE: (state, profileIsComplete: boolean) => {
		state.profileIsComplete = profileIsComplete;
	},

	SET_AUTH_DATA: (state, authData: AuthResponse) => {
		if (authData.connected_accounts) {
			const loggedInProviders: string[] = [];
			for (let i = 0; i < authData.connected_accounts.length; i++) {
				// @ts-ignore
				const { email, login_provider, is_entry_point } = authData.connected_accounts[i];

				if (is_entry_point) {
					Vue.set(state.info, 'email', email);
					Vue.set(state.info, 'loginProvider', login_provider);
				}
				loggedInProviders.push(login_provider);
			}
			Vue.set(state, 'loggedInProviders', loggedInProviders);
		} else {
			if (authData.connected_providers.length === 1) {
				Vue.set(state.info, 'loginProvider', authData.connected_providers[0]);
			}
			Vue.set(state, 'loggedInProviders', authData.connected_providers);
		}
		state.info.loginProvider = authData.loginProvider;

		Vue.set(state, 'jwLoginId', authData.login_id);
		Vue.set(state, 'accessToken', authData.token);
	},
	SET_AUTH_TOKEN: (state, token: string) => {
		state.accessToken = token;
	},

	SET_UC_TCF: (state, tcString: string | null) => {
		if (tcString) {
			state.uc_tcf = tcString;
		}
	},

	SET_USER_NAME(state, name) {
		Vue.set(state.info, 'name', name);
	},

	SET_USER_INFO(state, info) {
		Vue.set(state, 'info', info);
	},

	SET_ACCOUNT_CREATION_TIME(state, accountCreationTime: Date | undefined) {
		Vue.set(state, 'accountCreationTime', accountCreationTime);
	},

	SET_SETTINGS(state, settings) {
		// I WANNA KEEP THE SETTINGS STATE IN CASE WE NEEDED TO ROLLBACK
		Vue.set(state, 'settings', settings);
		// make sure we only update setting's providers if they change
		const settingsProvider = settings.providers || [];
		if (settingsProvider && JSON.stringify(state.settingsProvider) !== JSON.stringify(settingsProvider)) {
			state.settingsProvider = settingsProvider;
		}
		state.settingsJWConsents = settings.jw_consents || {};
		state.settingsUCConsents = settings.uc_consents || [];
	},

	LOGOUT: state => {
		Vue.delete(state, 'jwLoginId');
		Vue.delete(state, 'accessToken');
		Vue.set(state, 'info', {});
		const lastLoginProvider: string = state.loggedInProviders[0] || 'justwatch';
		Vue.set(state, 'lastUsedLoginProvider', lastLoginProvider);
		Vue.set(state, 'loggedInProviders', []);
		try {
			useUserStats().refetchUserStats();
		} catch (err) {
			import('@/helpers/sentry-helper').then(({ captureMessageForSentry }) =>
				captureMessageForSentry(
					'[user.store.ts] LOGOUT: Error refetching user stats:',
					{ err, where: 'user.store.ts' },
					'error'
				)
			);
		}
	},

	SET_LOGIN_PROVIDERS: (state, loginProviders: LoginProvider[]) => {
		state.loggedInProviders = loginProviders;
	},

	UPDATE_USER_INFO: (state, userInfo: UpdateUserInfoPayload) => {
		state.info = {
			...state.info,
			...userInfo,
		};
	},

	SET_PROFILE_INFO(state, profileInfo: ProfileInfo) {
		state.profileInfo = profileInfo;
	},

	SET_DISCOVERY_SETTINGS(state, discoverySettings: DiscoverySettings) {
		state.discoverySettings = discoverySettings;
	},

	SAVE_ONBOARDING_STATUS: (state, status) => {
		state.onboardingStatus = status;
	},

	SET_PERSISTED_OVERRIDDEN_LANGUAGE: (state, language) => {
		state.persistedOverriddenLanguage = language;
	},

	SET_PERSISTED_WEB_LOCALE: (state, webLocale) => {
		state.persistedWebLocale = webLocale;
	},

	SAVE_SEARCH_TERM: (state, searchTerm) => {
		let { recentSearchTerms } = state.searches;
		if (!recentSearchTerms.includes(searchTerm)) {
			// recentSearchTerms = [searchTerm, ...recentSearchTerms];
			recentSearchTerms = [searchTerm].concat(recentSearchTerms);
		} else {
			const index = recentSearchTerms.findIndex(term => term === searchTerm);
			const term = recentSearchTerms[index];
			recentSearchTerms.splice(0, 0, term);
			recentSearchTerms.splice(index + 1, 1);
		}

		// limit stored search terms to 10 to avoid store explosion
		state.searches.recentSearchTerms = recentSearchTerms.slice(0, RECENT_SEARCH_TERMS_COUNT);
	},

	SAVE_VISITED_TITLE_ID: (state, visitedTitleId: string) => {
		let { recentlyVisitedTitleIds } = state.searches;
		const containsTitle: boolean = recentlyVisitedTitleIds.some((elem: string) => elem === visitedTitleId);

		if (!containsTitle) {
			recentlyVisitedTitleIds = [visitedTitleId].concat(recentlyVisitedTitleIds);
		} else {
			const index = recentlyVisitedTitleIds.findIndex(titleId => titleId === visitedTitleId);
			const title = recentlyVisitedTitleIds[index];
			recentlyVisitedTitleIds.splice(0, 0, title);
			recentlyVisitedTitleIds.splice(index + 1, 1);
		}

		// limit stored search terms to 10 to avoid store explosion
		state.searches.recentlyVisitedTitleIds = recentlyVisitedTitleIds.slice(0, RECENT_SEARCH_TERMS_COUNT);
	},
	CLEAR_SEARCH_HISTORY: state => {
		state.searches.recentSearchTerms = [];
		state.searches.recentlyVisitedTitleIds = [];
	},
	SET_IS_PREMIUM: (state, value: boolean) => {
		state.isPremium = value;
	},
	SET_SUBSCRIPTION_INFO: (state, value: SubscriptionInfo) => {
		state.subscriptionInfo = value;
	},
	SET_HAS_CANCELLED_PREMIUM: (state, value: boolean) => {
		state.hasCancelledPremium = value;
	},
	SET_PREMIUM_PURCHASE_FLOW_STATE: (state, value: premiumPurchaseFlowState | null) => {
		state.premiumPurchaseFlowState = value;
	},
	SET_TIME_ZONE: (state, value: string) => {
		Vue.set(state, 'timeZone', value);
	},
	SET_HIDE_SEEN_DISLIKE_TITLES: (state, value: boolean) => {
		state.hideSeenDislikedTitles = value;
	},
	SET_IS_VERIFICATION_EMAIL_SENT: (state, isVerificationEmailSent: boolean) => {
		state.isVerificationEmailSent = isVerificationEmailSent;
	},
	SET_LAST_TIME_MOBILE_APP_BANNER_WAS_CLOSED_TS: (state, timeStamp: number) => {
		state.lastTimeMobileAppBannerWasClosedTS = timeStamp;
	},
	SET_LAST_TIME_LIST_ONBOARDING_WAS_SHOWN_TS: (state, timeStamp: number) => {
		state.lastTimeListsOnboardingWasShownTS = timeStamp;
	},
	SET_USERCENTRICS_DPS: (state, dataProcessingServices: { id: string; name: string }[]) => {
		state.usercentricsDPS = dataProcessingServices;
	},
	SET_USER_FIRST_VISIT: (state, value: boolean) => {
		state.isUserFirstVisit = value;
	},
	SET_PERMANENT_AUDIENCES_SUBGENRES(state, value) {
		state.permanentAudiencesSubgenres = value;
	},
	SET_USER_ROLE(state, value) {
		state.userRole = value;
	},
	TOGGLE_SHOW_OLD_TITLE_PAGE: state => {
		state.showOldTitlePage = !state.showOldTitlePage;
	},

	SET_ACQUIRED_EXPERIMENTS: (state, acquiredExperiments: UserState['acquiredExperiments']) => {
		Vue.set(state, 'acquiredExperiments', acquiredExperiments);
	},

	SET_NEW_PREFERRED_EXPERIMENTS: (state, preferredExperiments: UserState['_preferredExperiments']) => {
		Vue.set(state, '_preferredExperiments', preferredExperiments);
	},

	SET_ACTIVATED_EXPERIMENTS: (state, activatedExperiments: string[]) => {
		Vue.set(state, 'activatedExperiments', activatedExperiments);
	},

	ADD_ACTIVATED_EXPERIMENT: (state, experimentToActivate: string) => {
		if (!state.activatedExperiments.includes(experimentToActivate)) {
			state.activatedExperiments.push(experimentToActivate);
		}
	},
	SET_OPTED_OUT_CONSENT: (state, value: boolean) => {
		state.optedOutConsent = value;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
